import { buildLocationOptions } from "./locationFilters";
import { normalizeIndustrySelectionValues } from "./industrySelections";

const cleanText = (value) => String(value ?? "").replace(/\s+/g, " ").trim();

const normalizeKey = (value) => cleanText(value).toLowerCase();

const safeJsonParse = (value) => {
  if (typeof value !== "string" || !value.trim().startsWith("[")) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const COMPANY_MULTI_VALUE_SEPARATOR = " | ";
const PIPE_SEPARATED_VALUE_PATTERN = /\s*\|\s*/g;

const uniqSorted = (values = []) => {
  const seen = new Set();
  const next = [];

  values.forEach((value) => {
    const label = cleanText(value);
    const key = normalizeKey(label);
    if (!label || seen.has(key)) return;
    seen.add(key);
    next.push(label);
  });

  return next.sort((left, right) => left.localeCompare(right));
};

const splitTextValues = (
  value,
  { splitDelimitedStrings = true, splitCompoundValues = false } = {}
) => {
  if (Array.isArray(value)) {
    return value.flatMap((item) =>
      splitTextValues(item, { splitDelimitedStrings, splitCompoundValues })
    );
  }

  if (value && typeof value === "object") {
    return splitTextValues(
      value.label ??
        value.value ??
        value.name ??
        value.title ??
        value.skill ??
        value.location ??
        "",
      { splitDelimitedStrings, splitCompoundValues }
    );
  }

  const text = cleanText(value);
  if (!text) return [];

  const parsed = safeJsonParse(text);
  if (Array.isArray(parsed)) {
    return parsed.flatMap((item) =>
      splitTextValues(item, { splitDelimitedStrings, splitCompoundValues })
    );
  }

  if (splitCompoundValues && (text.includes(COMPANY_MULTI_VALUE_SEPARATOR) || text.includes("|"))) {
    return text
      .split(PIPE_SEPARATED_VALUE_PATTERN)
      .map((item) => cleanText(item))
      .filter(Boolean);
  }

  if (!splitDelimitedStrings) {
    return [text];
  }

  return text
    .split(/\s*(?:,|;|\||\n)\s*/g)
    .map((item) => cleanText(item))
    .filter(Boolean);
};

const getSourceRow = (row = {}) =>
  row?.raw && typeof row.raw === "object" ? row.raw : row;

export const buildTextOptionsFromRows = (
  rows = [],
  fields = [],
  { splitDelimitedStrings = true, splitCompoundValues = false } = {}
) =>
  uniqSorted(
    (Array.isArray(rows) ? rows : []).flatMap((row) => {
      const source = getSourceRow(row);
      return fields.flatMap((field) => [
        ...splitTextValues(row?.[field], { splitDelimitedStrings, splitCompoundValues }),
        ...splitTextValues(source?.[field], { splitDelimitedStrings, splitCompoundValues }),
      ]);
    })
  );

export const buildDetailFieldOptions = (
  rows = [],
  {
    industryFields = ["industry", "industry_sector", "company_industry", "industry_sectors_json"],
    sourceFields = ["source"],
    hardSkillFields = [
      "hard_skills",
      "skills",
      "skills_text",
      "skills_csv",
      "skills_json",
      "candidate_skills_json",
      "technology_stack",
      "recruitment_tools",
      "tech_stack",
      "tech_stack_tokens",
    ],
    softSkillFields = ["soft_skills", "job_boards"],
  } = {}
) => {
  const hardSkills = buildTextOptionsFromRows(rows, hardSkillFields);
  const softSkills = buildTextOptionsFromRows(rows, softSkillFields);
  const skills = uniqSorted([...hardSkills, ...softSkills]);
  const industries = uniqSorted(
    (Array.isArray(rows) ? rows : []).flatMap((row) => {
      const source = getSourceRow(row);
      return industryFields.flatMap((field) => [
        ...normalizeIndustrySelectionValues(row?.[field]),
        ...normalizeIndustrySelectionValues(source?.[field]),
      ]);
    })
  );
  const sources = buildTextOptionsFromRows(rows, sourceFields, {
    splitDelimitedStrings: false,
  });

  return {
    locations: buildLocationOptions(rows),
    industries,
    hardSkills,
    softSkills,
    skills,
    sources,
  };
};
